import { useMemo, useState } from 'react';
import { useChangePlanMutation } from 'src/store/services';
import log from 'src/utils/logger';
import { useGetUser, useSubscriptionInfo, useTeamInfo } from 'src/hooks';
import {
  AppRoutes,
  NinjaSubscriptionPlan,
  SubscriptionPlanInfo,
} from 'src/types';
import styles from './UpgradeDowngradeButton.module.scss';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { defineIsUpgrade, isSuperNinjaPlan } from 'src/utils';
import { UserTier } from 'src/types/models/UserTier';
import { convertTierName } from 'src/utils/user';
import { APPLE_PAYMENT_ERROR_TEXT } from 'src/constants';
import { env } from 'src/env';
import { useFeatureFlagEnabled } from 'posthog-js/react';

type UpgradeDowngradeButtonProps = {
  selectedPlan?: SubscriptionPlanInfo;
  numberOfSeats?: number;
  isLoading?: boolean;
};

export const UpgradeDowngradeButton = ({
  selectedPlan,
  numberOfSeats,
  isLoading,
}: UpgradeDowngradeButtonProps) => {
  const { tier_id, user_id, isTeamsTier } = useGetUser();
  const { teamInfo } = useTeamInfo();
  const navigate = useNavigate();
  const [changePlan] = useChangePlanMutation();
  const enableHandlingOfBusinessLogicInSN =
    useFeatureFlagEnabled('handleBusinessInSN');
  const canRedirectToSN = true;

  const { isAppleOrAmpPayment, subscriptionInfo, isLegacyPlan } =
    useSubscriptionInfo();

  const [isDisabledSubmit, setDisabledSubmit] = useState<boolean>(false);

  const isSNPlan =
    isSuperNinjaPlan(selectedPlan?.plan_family) && canRedirectToSN;

  const newTierName = useMemo(
    () =>
      selectedPlan?.plan_tier ? convertTierName(selectedPlan.plan_tier) : '',
    [selectedPlan],
  );

  const isOnlyPeriodChange =
    subscriptionInfo?.plan_tier === selectedPlan?.plan_tier &&
    subscriptionInfo?.interval !== selectedPlan?.period;

  const isUpgrade = defineIsUpgrade(
    tier_id as UserTier,
    selectedPlan?.plan_tier as NinjaSubscriptionPlan,
  );

  const handleConfirmAndPayClick = async () => {
    setDisabledSubmit(true);

    try {
      if (!!selectedPlan) {
        const data = await changePlan({
          user_id: user_id,
          product_key: selectedPlan.metadata?.stripe?.lookup_key || '',
          price_timestamp: selectedPlan.timestamp,
          seat_count: isTeamsTier ? teamInfo?.num_seats_total : numberOfSeats,
        }).unwrap();

        const redirectUrl = data?.approve_url;

        if (!!redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          if (isSNPlan || enableHandlingOfBusinessLogicInSN) {
            window.location.href = env.REACT_APP_SUPER_NINJA_URL;
          } else {
            navigate(AppRoutes.HOME);
          }
          if (isOnlyPeriodChange || isLegacyPlan) {
            toast.success('Payment processing in progress');
          } else {
            toast.success(
              isUpgrade
                ? `Upgrade to ${newTierName} successful`
                : 'Account downgrade started',
            );
          }
        }
      }
    } catch (e) {
      if (isOnlyPeriodChange || isLegacyPlan) {
        toast.error('Subscription update failed');
      } else {
        toast.error(
          isUpgrade
            ? `Upgrade to ${newTierName} failed`
            : 'Account downgrade failed',
        );
      }
      log.error();
    }
  };

  return (
    <button
      type="button"
      className={styles.confirmAndPayButton}
      onClick={handleConfirmAndPayClick}
      disabled={isDisabledSubmit || isLoading || isAppleOrAmpPayment}
      title={isAppleOrAmpPayment ? APPLE_PAYMENT_ERROR_TEXT : ''}
      data-e2e="confirm-and-pay-button"
    >
      Confirm & pay
    </button>
  );
};
